import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { SplitButton } from '@commonClient2/components';
import {
    ManagerBidAction, BidProfileStatus, WorkplanPublishStepCode, ScheduleSource, PublishStatusType, BidProcessType
} from '../../../Enums';
import { getWorkplanPublishStepCodeLabel } from '../../../Enums/Helpers/LabellingHelper';
import useBidProfileSplitButton from './useBidProfileSplitButton';
import useEnvironment from '../../../hooks/useEnvironment';
import * as api from '../../services/api-endpoints/workplan.service';
import { updateCompletedBidProfile } from '../../store/bidProfileManagement';
import { UPDATE_WORKPLAN_PUBLISH_PROGRESS } from '../../store/workplanPublishProgress';
import { getStepInfo } from '../../store/workplanPublishProgress/workplanPublishProgress.helper';

const INTERVAL_MS = 5000;

/** @param {{ className?: string }} param0 */
const BidProfileSplitButton = ({ className }) => {
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit.bidDashboard' });
    const dispatch = useDispatch();
    const { isProduction } = useEnvironment();

    /**
     * @type {ShiftBidding.ManagerStore}
     */
    // @ts-ignore
    const { managerAppData, bidProfile, bidProfileManagement } = useSelector((state) => state);
    const { bidProfiles, hyperfinds, managerBidSetups } = managerAppData;
    const {
        id,
        status,
        originalBidProfileInformation,
        bidProfileInformation,
        isPublishingToWorkplan,
        isRebid,
        isManagementAllowed,
    } = bidProfile;

    /**
     * @type {[number|undefined, Function]}
     */
    const [publishProgressValue, setPublishProgressValue] = useState(0);
    /**
     * @type {[WorkplanPublishStepCode, Function]}
     */
    const [progressStepCode, setProgressStepCode] = useState(WorkplanPublishStepCode.Loading);

    const isPublishDisabled = status !== BidProfileStatus.Closed || !isManagementAllowed;
    const isCancelDisabled = ![BidProfileStatus.Active, BidProfileStatus.Closed, BidProfileStatus.Scheduled].includes(status);
    
    const isSequentialBid = bidProfileInformation?.bidProcessType === BidProcessType.Sequential;
    
    const isForceStartAvailable = status === BidProfileStatus.Scheduled && !isProduction && !isSequentialBid;

    const isForceStartDisabled = !isForceStartAvailable || !isManagementAllowed;
    const isForceCloseAvailable = status === BidProfileStatus.Active && !isProduction && !isSequentialBid;
    const isForceCloseDisabled = !isForceCloseAvailable || !isManagementAllowed;

    const hasRolloutErrors = bidProfileManagement.rolloutErrors && bidProfileManagement.rolloutErrors.length > 0;
    
    /**
     * @type {BidProfileStatus[]}
     */
    const allowedStatusToShowRolloutDetails = [BidProfileStatus.Published];
    const isRolloutStatusDetailsDisabled = !allowedStatusToShowRolloutDetails.includes(status) && !hasRolloutErrors;

    const signOffAndPublish = useBidProfileSplitButton(id, isPublishingToWorkplan, { bidProfiles, hyperfinds, managerBidSetups });
    const options = [
        {
            label: t('publishBid'),
            value: ManagerBidAction.Publish,
            disabled: isPublishDisabled,
            isDisabledAndShowProgress: isPublishingToWorkplan,
        },
        {
            label: t('cancelBid'),
            value: ManagerBidAction.Cancel,
            disabled: isCancelDisabled,
        }
    ];

    if (isForceStartAvailable) {
        options.push({
            label: t('forceStartBid'),
            value: ManagerBidAction.ForceStart,
            disabled: isForceStartDisabled,
        });
    }

    if (isForceCloseAvailable) {
        options.push({
            label: t('forceCloseBid'),
            value: ManagerBidAction.ForceClose,
            disabled: isForceCloseDisabled,
        });
    }

    if ((status === BidProfileStatus.Published && originalBidProfileInformation.scheduleSource === ScheduleSource.WorkplanManager) || hasRolloutErrors) {
        options.push({
            label: t('rolloutStatusDetails'),
            value: ManagerBidAction.RolloutStatusDetails,
            disabled: isRolloutStatusDetailsDisabled,
        });
    }

    useEffect(() => {
        if (isPublishingToWorkplan) {
            const interval = setInterval(async () => {
                const result = await api.GetWorkplanPublishProgress(id);
                if (result.status === 200) {
                    const { progress, stepCode, isComplete } = result.data;
                    /**
                     * @type {number | undefined}
                     */
                    const progressValue = progress;
                    const stepInfo = getStepInfo(stepCode, isRebid, bidProfileManagement);
                    const hasErrors = stepInfo.find((x) => x.errors.length >= 1);

                    if (isComplete) {
                        dispatch(updateCompletedBidProfile(id, { bidProfiles, hyperfinds }));
                    }
                    setProgressStepCode(stepCode);
                    setPublishProgressValue(progressValue);

                    /** @type {PublishStatusType} */
                    let publishStatus = PublishStatusType.Success;

                    if (!isComplete) {
                        publishStatus = PublishStatusType.Publishing;
                    } else if (hasErrors) {
                        publishStatus = PublishStatusType.Error;
                    }

                    dispatch({
                        type: UPDATE_WORKPLAN_PUBLISH_PROGRESS,
                        payload: {
                            progressValue,
                            progressStepCode: stepCode,
                            isComplete,
                            stepInfo,
                            status: publishStatus,
                        },
                    });
                }
            }, INTERVAL_MS);

            return () => clearInterval(interval);
        }
    }, [isPublishingToWorkplan]);

    return (
        <SplitButton
            options={options}
            className={`bid-profile-split-button ${className || ''}`}
            color="primary"
            handleClick={signOffAndPublish}
            disabled={isPublishDisabled && isCancelDisabled && isForceStartDisabled && isForceCloseDisabled && isRolloutStatusDetailsDisabled}
            defaultValue={isPublishDisabled ? options[1] : options[0]}
            progressLabel={getWorkplanPublishStepCodeLabel(progressStepCode)}
            progressValue={publishProgressValue}
        />
    );
};

export default BidProfileSplitButton;
